import { useState, useEffect } from 'react';
import { Plus, Smartphone, Database, Check, ChevronRight, Settings, Layout, AlignLeft, Store, Trash2 } from 'lucide-react';
import { apiClient } from '@dukene/shared';

interface Flow {
    id: string;
    name: string;
    status: string;
    whatsappId?: string;
    config: any;
}

export const FlowBuilder = ({ storeId }: { storeId?: string }) => {
    const [flows, setFlows] = useState<Flow[]>([]);
    const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (storeId) fetchFlows();
    }, [storeId]);

    const fetchFlows = async () => {
        if (!storeId) return;
        try {
            // Assume we create a GET /api/v1/whatsapp/flows/list?storeId= endpoint
            const response: any = await apiClient.get(`/whatsapp/flows/list?storeId=${storeId}`);
            setFlows(response?.data || response || []);
        } catch (error) {
            console.error('Error fetching flows:', error);
        }
    };

    const createNewFlow = () => {
        const newFlow: Flow = {
            id: `temp_${Date.now()}`,
            name: 'New Custom Flow',
            status: 'DRAFT',
            config: {
                steps: [
                    {
                        id: "START_SCREEN",
                        title: "Welcome",
                        header: "Welcome to our store",
                        type: "form",
                        fields: [],
                        isTerminal: true,
                        buttonLabel: "Submit"
                    }
                ]
            }
        };
        setSelectedFlow(newFlow);
    };

    const saveFlow = async () => {
        if (!storeId || !selectedFlow) return;
        setLoading(true);
        try {
            await apiClient.post('/whatsapp/flows/save', {
                storeId,
                name: selectedFlow.name,
                config: selectedFlow.config
            });
            alert('Flow saved successfully!');
            fetchFlows();
        } catch (error) {
            console.error('Error saving flow:', error);
            alert('Failed to save flow');
        } finally {
            setLoading(false);
        }
    };

    if (!selectedFlow) {
        return (
            <div className="flex-1 p-8 bg-gray-50 h-full overflow-y-auto">
                <div className="max-w-5xl mx-auto">
                    <div className="flex justify-between items-end mb-8">
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-2">WhatsApp Flows</h2>
                            <p className="text-gray-500">Design interactive forms and workflows that customers can fill out right inside WhatsApp.</p>
                        </div>
                        <button onClick={createNewFlow} className="bg-black text-white px-5 py-2.5 rounded-xl font-medium hover:bg-gray-900 transition-colors flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Create New Flow
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {flows.map(flow => (
                            <div key={flow.id} onClick={() => setSelectedFlow(flow)} className="bg-white border border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-black hover:shadow-md transition-all group">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                                        <Layout className="w-5 h-5" />
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider ${flow.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {flow.status}
                                    </span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-black transition-colors">{flow.name}</h3>
                                <p className="text-sm text-gray-500 line-clamp-2">{(flow.config?.screens?.length || 0)} screens defined</p>
                            </div>
                        ))}
                    </div>

                    {flows.length === 0 && (
                        <div className="text-center py-20 bg-white border border-gray-200 rounded-3xl mt-4">
                            <Smartphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No flows created yet</h3>
                            <p className="text-gray-500 mb-6 max-w-sm mx-auto">Create interactive forms to collect leads, feedback, or book appointments directly in WhatsApp.</p>
                            <button onClick={createNewFlow} className="bg-black text-white px-6 py-3 rounded-xl font-medium hover:bg-gray-900 transition-colors inline-flex items-center gap-2">
                                <Plus className="w-4 h-4" />
                                Create Your First Flow
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const isPublished = selectedFlow.status === 'PUBLISHED';
    const activeStep = selectedFlow.config.steps?.[0] || selectedFlow.config.screens?.[0]; // Fallback for old flows

    const addField = (type: string) => {
        if (isPublished) return;
        const newFlow = { ...selectedFlow };
        if (!newFlow.config.steps) return;
        const currentStep = newFlow.config.steps[0];
        if (!currentStep.fields) currentStep.fields = [];
        
        currentStep.fields.push({
            type,
            name: `field_${Date.now()}`,
            label: type === 'text' ? 'New Input' : type === 'select' ? 'New Dropdown' : 'New Field',
            required: false,
            options: type === 'select' ? [{ id: '1', title: 'Option 1' }] : undefined
        });
        setSelectedFlow(newFlow);
    };

    const duplicateFlow = () => {
        const newFlow = {
            ...selectedFlow,
            id: `temp_${Date.now()}`,
            name: `${selectedFlow.name} (Copy)`,
            status: 'DRAFT'
        };
        setSelectedFlow(newFlow);
    };

    return (
        <div className="flex h-full bg-white overflow-hidden">
            {/* Left Sidebar: Components */}
            <div className="w-72 border-r border-gray-200 bg-gray-50 flex flex-col">
                <div className="p-4 border-b border-gray-200 bg-white">
                    <button onClick={() => setSelectedFlow(null)} className="text-sm text-gray-500 hover:text-black flex items-center gap-1 mb-4 transition-colors">
                        <ChevronRight className="w-4 h-4 rotate-180" /> Back to flows
                    </button>
                    <input 
                        type="text" 
                        value={selectedFlow.name}
                        onChange={(e) => {
                            if (!isPublished) setSelectedFlow({...selectedFlow, name: e.target.value})
                        }}
                        readOnly={isPublished}
                        className="text-xl font-bold bg-transparent outline-none w-full placeholder:text-gray-300"
                        placeholder="Flow Name"
                    />
                </div>
                
                <div className="p-4 flex-1 overflow-y-auto">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Add Components</h4>
                    
                    <div className="space-y-2">
                        <button onClick={() => addField('text')} disabled={isPublished} className="w-full bg-white border border-gray-200 p-3 rounded-xl flex items-center gap-3 cursor-pointer hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <AlignLeft className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium">Text Input</span>
                        </button>
                        <button onClick={() => addField('select')} disabled={isPublished} className="w-full bg-white border border-gray-200 p-3 rounded-xl flex items-center gap-3 cursor-pointer hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <Check className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium">Dropdown</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Center: Canvas (Phone Preview) */}
            <div className="flex-1 bg-gray-100 p-8 flex justify-center overflow-y-auto relative">
                {isPublished && (
                    <div className="absolute top-4 bg-amber-100 text-amber-800 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm">
                        <Settings className="w-4 h-4" /> This flow is published and cannot be structurally edited. Duplicate to make changes.
                    </div>
                )}
                <div className="w-[360px] min-h-[640px] bg-white rounded-[3rem] shadow-2xl border-8 border-gray-900 overflow-hidden relative flex flex-col mt-4">
                    {/* Phone Header */}
                    <div className="bg-[#075e54] text-white p-4 pt-10 shadow-md z-10 flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                            <Store className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="font-medium leading-none">{activeStep?.title || 'Screen'}</p>
                            <p className="text-[10px] text-white/70 mt-1">Flow preview</p>
                        </div>
                    </div>
                    
                    {/* Flow Content */}
                    <div className="flex-1 bg-gray-50 p-4 overflow-y-auto space-y-4">
                        {activeStep?.header && (
                            <h2 className="text-lg font-bold text-gray-900">{activeStep.header}</h2>
                        )}
                        {activeStep?.fields?.map((field: any, idx: number) => {
                            if (field.type === 'text') {
                                return (
                                    <div key={idx} className="relative group">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">{field.label}</label>
                                        <input type="text" disabled className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm" placeholder="User input..." />
                                    </div>
                                )
                            }
                            if (field.type === 'select') {
                                return (
                                    <div key={idx} className="relative group">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">{field.label}</label>
                                        <select disabled className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-500">
                                            <option>Select an option...</option>
                                        </select>
                                    </div>
                                )
                            }
                            return null;
                        })}
                    </div>
                    
                    {/* Footer */}
                    <div className="p-4 bg-white border-t border-gray-200">
                        <button className="w-full bg-[#00a884] text-white py-3 rounded-full font-medium text-sm shadow-sm">
                            {activeStep?.buttonLabel || 'Submit'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Right Sidebar: Properties & Data Binding */}
            <div className="w-80 border-l border-gray-200 bg-white flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h3 className="font-semibold flex items-center gap-2">
                        <Settings className="w-4 h-4" /> Properties
                    </h3>
                    {isPublished ? (
                        <button onClick={duplicateFlow} className="bg-amber-100 text-amber-900 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors flex items-center gap-2">
                            Duplicate Flow
                        </button>
                    ) : (
                        <button onClick={saveFlow} disabled={loading} className="bg-black text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors flex items-center gap-2">
                            {loading ? 'Saving...' : 'Save Flow'}
                        </button>
                    )}
                </div>
                
                <div className="p-6 flex-1 overflow-y-auto">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Screen Settings</h4>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Screen Title</label>
                            <input 
                                type="text" 
                                value={activeStep?.title || ''} 
                                readOnly={isPublished}
                                onChange={(e) => {
                                    if (isPublished) return;
                                    const newFlow = {...selectedFlow};
                                    newFlow.config.steps[0].title = e.target.value;
                                    setSelectedFlow(newFlow);
                                }}
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-black transition-colors" 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Header Text</label>
                            <input 
                                type="text" 
                                value={activeStep?.header || ''} 
                                readOnly={isPublished}
                                onChange={(e) => {
                                    if (isPublished) return;
                                    const newFlow = {...selectedFlow};
                                    newFlow.config.steps[0].header = e.target.value;
                                    setSelectedFlow(newFlow);
                                }}
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-black transition-colors" 
                            />
                        </div>
                    </div>
                    
                    <div className="mt-8 border-t border-gray-100 pt-6">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                            <Database className="w-3.5 h-3.5" /> Field Definitions
                        </h4>
                        <div className="space-y-4">
                            {activeStep?.fields?.map((field: any, idx: number) => (
                                <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase">{field.type}</span>
                                        {!isPublished && (
                                            <button 
                                                onClick={() => {
                                                    const newFlow = {...selectedFlow};
                                                    newFlow.config.steps[0].fields.splice(idx, 1);
                                                    setSelectedFlow(newFlow);
                                                }}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    <input 
                                        type="text" 
                                        value={field.label}
                                        readOnly={isPublished}
                                        onChange={(e) => {
                                            if (isPublished) return;
                                            const newFlow = {...selectedFlow};
                                            newFlow.config.steps[0].fields[idx].label = e.target.value;
                                            setSelectedFlow(newFlow);
                                        }}
                                        placeholder="Label"
                                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs mb-2 outline-none" 
                                    />
                                    <input 
                                        type="text" 
                                        value={field.name}
                                        readOnly={isPublished}
                                        onChange={(e) => {
                                            if (isPublished) return;
                                            const newFlow = {...selectedFlow};
                                            newFlow.config.steps[0].fields[idx].name = e.target.value;
                                            setSelectedFlow(newFlow);
                                        }}
                                        placeholder="Variable Name (e.g. email)"
                                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono outline-none" 
                                    />
                                </div>
                            ))}
                            {(!activeStep?.fields || activeStep.fields.length === 0) && (
                                <p className="text-xs text-gray-400 italic">No fields added yet. Use the left panel to add fields.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
